import { v4 } from 'uuid';
import {
  CREATE_COMMUNITY,
  CreateCommunityResponse,
} from 'src/configs/APISchemas/CreateCommunity';
import {
  UPDATE_APPLICATION,
  UpdateApplicationResponse,
} from 'src/configs/APISchemas/UpdateApplication';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import {
  PurchasePolicyAdditionalData,
  PurchasePolicyInput,
} from 'src/redux/reducer/v2/purchasePolicy';
import { SharedReducer } from 'src/redux/reducer/v2/shared';
import { ErrorOutput } from 'src/types';

const getDefaultErrorOutput = (message: string): ErrorOutput => ({
  id: v4(),
  message,
  type: 'error',
});

interface UpdateCoverageDateErrorResult {
  errors: ErrorOutput[];
  data?: never;
}

interface UpdateCoverageDateSuccessResult {
  errors?: never;
  data: PurchasePolicyInput;
}

export type UpdateCoverageDateResult =
  | UpdateCoverageDateErrorResult
  | UpdateCoverageDateSuccessResult;

export async function updateCoverageDate({
  companyVendor,
  coverageStart,
  purchasePolicyAdditionalData,
  purchasePolicyInput,
}: {
  purchasePolicyInput: PurchasePolicyInput;
  coverageStart: string;
  purchasePolicyAdditionalData: PurchasePolicyAdditionalData;
  companyVendor: SharedReducer['companyVendor'];
}): Promise<UpdateCoverageDateResult> {
  const buildingAddress = purchasePolicyInput.buildingAddress.value;
  const buildingAddressStreet = Boolean(companyVendor)
    ? buildingAddress.street
    : `${buildingAddress.addressNumber || ''} ${buildingAddress.street}`;
  const buildingAddressYearBuilt = parseInt(
    buildingAddress['yearBuilt'] || '2000',
    10,
  );

  const createCommunityRequestMutation = CREATE_COMMUNITY(
    {
      communityName:
        purchasePolicyInput.buildingName ||
        purchasePolicyInput.addressName ||
        '',
      coverageStart: purchasePolicyInput.coverageStart,
      buildingId: purchasePolicyInput.buildingId,
      building: {
        yearBuilt: buildingAddressYearBuilt,
        zipCode: buildingAddress.postalCode,
        addressState: buildingAddress.state,
        addressCity: buildingAddress.municipality,
        addressStreet: buildingAddressStreet,
      },
    },
    purchasePolicyAdditionalData.id,
  );

  const createCommunityResponse = await GraphQLRequest<CreateCommunityResponse>(
    createCommunityRequestMutation,
  );

  const createCommunityData = createCommunityResponse.createCommunity;

  if ('errors' in createCommunityData) {
    return {
      errors: createCommunityData.errors,
    };
  }

  const updateApplicationRequestMutation = UPDATE_APPLICATION(
    {
      ...purchasePolicyInput,
      coverageStart,
    },
    purchasePolicyAdditionalData,
  );

  const updateApplicationResponse =
    await GraphQLRequest<UpdateApplicationResponse>(
      updateApplicationRequestMutation,
    );

  const updateApplicationData = updateApplicationResponse.updateApplication;

  if (!updateApplicationData) {
    const errorOutput = getDefaultErrorOutput(
      'An error occurred while updating the application',
    );

    return {
      errors: [errorOutput],
    };
  }

  if ('errors' in updateApplicationData) {
    return {
      errors: updateApplicationData.errors,
    };
  }

  return {
    data: {
      ...purchasePolicyInput,
      ...updateApplicationData,
      ...createCommunityData,
      coverageStart,
    },
  };
}
